import { Icon } from '@gitbook/spine-icons';
import { BadgeLink, type BadgeProps } from '@gitbook/spine-react';

import { isSelfServeMigrateAction, isSiteUpgradeAction, type UseUpgradeSite } from '../../state';

/**
 * A small badge indicating which plan is required based on the given site `upgrade`.
 *
 * The component will not render if the given upgrade is not a site upgrade action.
 *
 * Can, optionally, render as an inactive paid feature indicator. Used to warn the
 * user that the feature is enabled and can be disabled, but an upgrade will be
 * required to enable it again.
 */
export function PaidFeatureIndicator({
    upgrade,
    isFeatureActive,
    iconOnly,
    size,
}: {
    upgrade: UseUpgradeSite | null;
    /**
     * `danger` colored and Icon.Warning, instead of `upgrade` colored and Icon.Upgrade
     * the tooltip explains that the feature, if disabled, will require an upgrade to
     * be re-enabled.
     */
    isFeatureActive?: boolean;
} & Pick<BadgeProps, 'iconOnly' | 'thin' | 'size'>) {
    if (isSelfServeMigrateAction(upgrade)) {
        return (
            <BadgeLink
                {...getPaidIndicatorBadgeProps({ upgrade, iconOnly, size, isFeatureActive })}
                tooltip={
                    isFeatureActive
                        ? `This feature is only available on our latest pricing. If disabled, you'll need to upgrade to regain access to it.`
                        : 'This feature is only available on our latest pricing. Upgrade now to gain access to it.'
                }
                onPress={upgrade.onPress}
            />
        );
    }

    if (!isSiteUpgradeAction(upgrade)) {
        return null;
    }

    // Alternative version, shown when a paid feature is enabled but a site upgrade is required.
    if (isFeatureActive) {
        return (
            <BadgeLink
                {...getPaidIndicatorBadgeProps({ upgrade, iconOnly, size, isFeatureActive })}
                tooltip={`This feature is part of the ${upgrade.plan} plan. You need to upgrade to re-enable it.`}
                onPress={upgrade.onPress}
            />
        );
    }

    return (
        <BadgeLink
            {...getPaidIndicatorBadgeProps({ upgrade, iconOnly, size })}
            tooltip={`Available on the ${upgrade.plan} plan. Click to upgrade.`}
            onPress={upgrade.onPress}
        />
    );
}

/**
 * Returns badge props for the PaidFeatureIndicator BadgeLink components.
 * Some components, like Picker or Palette button, may use this function for their `badge` prop.
 *
 */
export function getPaidIndicatorBadgeProps({
    upgrade,
    isFeatureActive,
    ...badgeProps
}: BadgeProps & {
    upgrade: UseUpgradeSite | null;
    // `danger` colored and Icon.Warning, instead of `upgrade` colored and Icon.Upgrade
    isFeatureActive?: boolean;
}): BadgeProps | undefined {
    let commonProps: BadgeProps = {
        size: badgeProps.size ? badgeProps.size : !badgeProps.iconOnly ? 'small' : 'medium',
        thin: !badgeProps.iconOnly,
    };

    if (isSelfServeMigrateAction(upgrade)) {
        return {
            kind: isFeatureActive ? 'danger' : 'upgrade',
            icon: isFeatureActive ? Icon.Warning : Icon.Upgrade,
            label: !badgeProps.iconOnly ? 'New pricing' : undefined,
            ...commonProps,
            ...badgeProps,
        };
    }

    if (!isSiteUpgradeAction(upgrade)) {
        return undefined;
    }

    commonProps = {
        size: badgeProps.size ? badgeProps.size : !badgeProps.iconOnly ? 'small' : 'medium',
        label: !badgeProps.iconOnly ? upgrade?.plan : undefined,
        thin: !badgeProps.iconOnly,
    };

    if (isFeatureActive) {
        return {
            kind: 'danger',
            icon: Icon.Warning,
            ...commonProps,
            ...badgeProps,
        };
    }

    return {
        kind: 'upgrade',
        icon: Icon.Upgrade,
        ...commonProps,
        ...badgeProps,
    };
}
